import { orderBy, sortBy } from 'lodash';
import { createCachedSelector, createStructuredCachedSelector } from 're-reselect';
import { TypedUseSelectorHook, useSelector as untypedUseSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { Horse } from '~/board';
import { DEFAULT_FOLLOW_ZOOM_STOPS } from '~/board/cameraZoom';
import { getResultsMockData } from '~/end/shared';
import { CardValue } from '~/overlay';
import type { StateMachineValue } from '~/server/src/tmmodel';
import Turfmaster from '~/server/src/tmmodel';

import { Position } from './models';
import { State } from './store';

export const useSelector: TypedUseSelectorHook<State> = untypedUseSelector;

/* Connection selectors */
export const selectUserId = (state: State) => state.connection.userId;
export const selectServerTime = (state: State) => state.connection.serverTime;

/* Setup selectors */
export const selectGameId = (state: State) => state.setup.gameId;
export const selectGameName = (state: State) => state.setup.gameName;
export const selectGameType = (state: State) => state.setup.gameType;
export const selectGameStartDate = (state: State) => state.setup.gameStartDate;
export const selectSetupGames = (state: State) => state.setup.games;
export const selectSetupGamesSorted = createCachedSelector(selectSetupGames, (games) =>
  orderBy(games, (game) => new Date(game.timeCreated).getTime(), 'desc'),
)(() => 'gamesSorted');
export const selectSetupTournaments = (state: State) => state.setup.tournaments;
export const selectHorseAmount = (state: State) => state.setup.horseAmount;
export const selectIsNewGame = (state: State) => state.setup.isNewGame;
export const selectDebugSpawnSource = (state: State) => state.setup.debugSpawnSource;
export const selectRestoreState = (state: State) => state.setup.restoreState;
export const selectRejoinState = (state: State) => state.setup.rejoinState;
export const selectSetupError = (state: State) => state.setup.setupError;
export const selectIsRefreshingFreeGames = (state: State) => state.setup.isRefreshingFreeGames;
export const selectIsRefreshingTournaments = (state: State) => state.setup.isRefreshingTournaments;

/* Board selectors */
export const selectHurdles = (state: State) => state.board.hurdles;
export const selectMoves = (state: State) => state.board.moves;
export const selectHorses = (state: State) => state.board.horses;
export const selectHorse = (state: State, index: number): Horse | undefined =>
  state.board.horses[index];
export const selectHorsesCount = (state: State) => state.board.horses.length;
export const selectHorsesField = (state: State) => state.board.horseFields;
export const selectHorseField = (state: State, index: number) => state.board.horseFields[index];
export const selectCameraPosition = (state: State) => state.board.camera as Position;

/* Camera state for mode-aware logic (prefer over selectCameraPosition when mode/factor matter) */
export const selectCamera = (state: State) => state.board.camera;
export const selectCameraMode = (state: State) => state.board.camera.mode;
export const selectFollowZoomStops = (state: State) =>
  state.board.camera.followZoomStops ?? DEFAULT_FOLLOW_ZOOM_STOPS;

/* Game selectors */
export const selectConnected = (state: State) => state.game.connected;
export const selectConnectionTimeout = (state: State) => state.game.connectionTimeout;
export const selectSpectatorStatus = (state: State) => state.game.spectatorStatus;
/** True when viewing a game as spectator (LIVE pill visible); used for left-panel top inset. */
export const selectIsSpectatorMode = (state: State) =>
  state.game.spectatorStatus === 'joined' || state.game.spectatorStatus === 'connecting';
export const selectLeaveStatus = (state: State) => state.game.leaveStatus;
export const selectLeaveAttempt = (state: State) => state.game.leaveAttempt;
export const selectLeaveError = (state: State) => state.game.leaveError;
export const selectCurrentState = (state: State) => state.game.currentState;
export const selectRound = (state: State) => state.game.round;

export const selectTurnPos = (state: State) => state.game.turnpos;
export const selectTurnOrder = (state: State) => state.game.turnorder;
export const selectTurnDices = (state: State) => state.game.turndices;
export const selectTurnDicesCur = (state: State) => state.game.turndicescur;
export const selectTurnType = (state: State) => state.game.turntype;
export const selectTurnDicesPlayerId = (state: State) => state.game.turndiceplayerid;

export const selectPlayerId = (state: State) => state.setup.playerId;
export const selectPlayers = (state: State) => state.game.players;
export const selectPlayer = (state: State, index: number) => state.game.players[index];
export const selectPlayerName = (state: State, index: number) =>
  state.game.players[index] ? state.game.players[index].name : '';
export const selectPlayerCountry = (state: State, index: number) =>
  state.game.players[index] ? state.game.players[index].country : undefined;

export const selectHorsesRanks = (state: State) => state.game.horsesRanks;
export const selectHorseRanks = (state: State, index: number) => state.game.horsesRanks[index];
export const selectHorsesHandi = (state: State) => state.game.horsesHandi;
export const selectHorseHandi = (state: State, index: number) => state.game.horsesHandi[index];
export const selectHorsesPoints = (state: State) => state.game.horsesPoints;
export const selectHorsePoints = (state: State, index: number) => state.game.horsesPoints[index];
export const selectHorsesFinished = (state: State) => state.game.horsesFinished;
export const selectHorseFinished = (state: State, index: number) =>
  state.game.horsesFinished[index];
export const selectHorsesDropped = (state: State) => state.game.horsesDropped;
export const selectHorseDropped = (state: State, index: number) => state.game.horsesDropped[index];
export const selectHorsesStarted = (state: State) => state.game.horsesStarted;
export const selectHorsesBehindGoal = (state: State) => state.game.horsesBehindGoal;

export const preGameStates: readonly StateMachineValue[] = [
  Turfmaster.stateMachine.GETGAMES,
  Turfmaster.stateMachine.NEWGAME,
  Turfmaster.stateMachine.RESTOREGAME,
  Turfmaster.stateMachine.JOINGAME,
  Turfmaster.stateMachine.JOINGAMEOPP,
  Turfmaster.stateMachine.LEAVEGAME,
  Turfmaster.stateMachine.LEAVEGAMEOPP,
  Turfmaster.stateMachine.GETTOURNAMENTS,
  Turfmaster.stateMachine.JOINTOURNAMENT,
];
export const gameStates = Object.values(Turfmaster.stateMachine).filter(
  (state) => !preGameStates.includes(state),
);
export const selectIsGameRunning = (state: State) =>
  !preGameStates.includes(state.game.currentState);

/**
 * Phase helpers
 *
 * Important: we intentionally model the "pre-game" boundary as an explicit allowlist.
 * Everything not in `preGameStates` is treated as "gameplay has started" even if
 * the round counter is still 0 (transitional states like STARTGAME/STARTROUND/STARTTURN).
 */
export const isPreGameState = (currentState: StateMachineValue) =>
  preGameStates.includes(currentState);

export const selectIsPreGameState = createSelector(selectCurrentState, (currentState) =>
  preGameStates.includes(currentState),
);

export const selectHasGameplayStarted = createSelector(
  selectIsPreGameState,
  selectRound,
  (isPreGame, round) => round > 0 || !isPreGame,
);

// "Interactive" states: player/turn UI is active (cards/dice/move/merge).
const INTERACTIVE_GAMEPLAY_STATES = [
  Turfmaster.stateMachine.SELECTCARDDICE,
  Turfmaster.stateMachine.ADDBONUSCARDSOPP,
  Turfmaster.stateMachine.SELECTMOVE,
  Turfmaster.stateMachine.SELECTDICES,
  Turfmaster.stateMachine.MERGECARDS,
] as const;

export const isInteractiveGameplayState = (currentState: StateMachineValue) =>
  INTERACTIVE_GAMEPLAY_STATES.includes(
    currentState as (typeof INTERACTIVE_GAMEPLAY_STATES)[number],
  );

export const isMergeCardsState = (currentState: StateMachineValue) =>
  currentState === Turfmaster.stateMachine.MERGECARDS ||
  currentState === Turfmaster.stateMachine.MERGECARDSOPP;

export const selectIsInteractiveGameplayState = createSelector(selectCurrentState, (currentState) =>
  INTERACTIVE_GAMEPLAY_STATES.includes(
    currentState as (typeof INTERACTIVE_GAMEPLAY_STATES)[number],
  ),
);

/**
 * True while the game is in a dice interaction state where horse highlighting is distracting.
 * Keep this narrow and state-based so board highlighting does not depend on local UI timers.
 */
export const selectIsDiceInteractionPhase = createSelector(
  selectCurrentState,
  selectTurnType,
  (currentState, turnType) =>
    turnType === Turfmaster.turntypes.DICES &&
    (currentState === Turfmaster.stateMachine.STARTTURN ||
      currentState === Turfmaster.stateMachine.SELECTDICES ||
      currentState === Turfmaster.stateMachine.SELECTDICESOPP ||
      currentState === Turfmaster.stateMachine.SELECTCARDDICE),
);

/* Overlay selectors */
export const selectHandiFailureHorseIndex = (state: State) => state.overlay.handiFailureHorseIndex;
export const selectCardDice = (state: State) => state.overlay.cardDice;
export const selectLastCardHorseIndex = (state: State) => state.overlay.lastCardHorseIndex;
export const selectLastCard = (state: State) => state.overlay.lastCard;
export const selectLastTurnDicePlayerId = (state: State) => state.overlay.lastTurnDicePlayerId;
export const selectLastTurnDices = (state: State) => state.overlay.lastTurnDices;
export const selectLastTurnDicesCur = (state: State) => state.overlay.lastTurnDicesCur;

export const selectHorsesCards = (state: State) => state.overlay.horsesCards;
export const selectHorseCards = (state: State, horseIndex: number) =>
  state.overlay.horsesCards[horseIndex] as CardValue[][] | undefined;
export const selectHorsesBonuscards = (state: State) => state.overlay.horsesBonusCards;
export const selectHorseBonuscards = (state: State, horseIndex: number) =>
  state.overlay.horsesBonusCards[horseIndex];
export const selectOwnHorseRemovedCards = (state: State) =>
  state.overlay.ownHorseRemovedCards as CardValue[];

export const selectSelectedDicesLocal = (state: State) => state.overlay.selectedDicesLocal;
export const selectSelectedCardIndexLocal = (state: State) => state.overlay.selectedCardIndexLocal;

export const selectEventLog = (state: State) => state.overlay.eventLog;
export const selectChatLastReadIncomingEventIndex = (state: State) =>
  state.overlay.chatLastReadIncomingEventIndex;
export const selectLatestGameStateAnchor = (state: State) => state.overlay.latestGameStateAnchor;
export const selectSelectedStep = (state: State) => state.overlay.selectedStep;
export const selectHistoryCursor = (state: State) => state.overlay.historyCursor;
export const selectIsHistoryMode = createSelector(
  selectHistoryCursor,
  (historyCursor) => historyCursor.kind !== 'live',
);
export const selectPanelState = (state: State) => state.overlay.panelState;
export const selectHistoryPanelState = (state: State) => state.overlay.panelState.history;
export const selectChatPanelState = (state: State) => state.overlay.panelState.chat;

/** True when any left-side panel is open (history, debug, or TanStack devtools). */
export const selectLeftPanelOpen = createSelector(
  (state: State) => state.overlay.panelState.history.isVisible,
  (state: State) => state.overlay.panelState.debug.isVisible,
  (state: State) => state.overlay.panelState.tanstackDevtoolsOpen,
  (history, debug, tanstack) => history || debug || tanstack,
);

/** True when the chat panel is open (right side). */
export const selectRightPanelOpen = (state: State) => state.overlay.panelState.chat.isVisible;

// Event types that should be displayed in the chat
// Note: TM_REJOINGAME is intentionally excluded - it's logged for debugging/auditing
// but not displayed to users since browser refresh/reconnect is not meaningful for others
const DISPLAYABLE_EVENT_TYPES = [
  'NEWMESSAGE',
  'JOINGAMEOPP',
  'LEAVEGAMEOPP',
  'STARTGAME',
  'STARTROUND',
  'ENDROUND',
  'ENDGAME',
  'TM_GAMECREATED',
] as const;

/**
 * Synthetic, chat-only event markers emitted by `selectChatDisplayItems`.
 * These let the render layer stay dumb while still supporting combined phrasing.
 */
export const CHAT_EVENT_TYPES = {
  GAME_STARTED_ROUND_1: 'CHAT_GAME_STARTED_ROUND_1',
  ROUND_ENDED_GAME_FINISHED: 'CHAT_ROUND_ENDED_GAME_FINISHED',
} as const;

/** Local chat message model (replaces @chatscope MessageModel). */
export interface ChatMessageModel {
  message: string;
  sentTime: string;
  position: 'first' | 'normal' | 'last' | 'single';
  direction: 'incoming' | 'outgoing';
  sender: string;
}

export interface ChatDisplayItem {
  type: 'message' | 'event';
  id: string;
  timestamp: string;
  // For messages
  model?: ChatMessageModel;
  userId?: string;
  message?: string;
  /**
   * Index into `overlay.eventLog` for `NEWMESSAGE` rows.
   * Used to advance `chatLastReadIncomingEventIndex` precisely.
   */
  eventLogIndex?: number;
  /** True when the message was sent by a spectator (not a player in the game). */
  isSpectator?: boolean;
  // For events
  eventType?: string;
  payload?: Record<string, unknown>;
  senderId?: string;
}

export const selectChatDisplayItems = createSelector(
  selectEventLog,
  selectUserId,
  selectPlayers,
  (eventLog, userId, players) => {
    type IndexedEntry = (typeof eventLog)[number] & { eventLogIndex: number };
    const sortedEntries = sortBy(
      eventLog
        .map((entry, eventLogIndex) => ({
          ...entry,
          eventLogIndex,
        }))
        .filter((entry) =>
          (DISPLAYABLE_EVENT_TYPES as readonly string[]).includes(entry.eventType),
        ),
      (entry: IndexedEntry) => new Date(entry.timestamp).getTime(),
      'asc',
    );

    type SyntheticChatEventType = (typeof CHAT_EVENT_TYPES)[keyof typeof CHAT_EVENT_TYPES];
    type NormalizedEntry = Omit<(typeof sortedEntries)[number], 'eventType'> & {
      eventType: (typeof sortedEntries)[number]['eventType'] | SyntheticChatEventType;
      /** Stable key material derived from source events for synthetic rows. */
      chatSyntheticKey?: string;
    };

    /**
     * Normalize sorted chat entries into a display list.
     *
     * Merge rules (chat-only, adjacency-based):
     * - STARTGAME + STARTROUND(round=0) => CHAT_GAME_STARTED_ROUND_1 (skip redundant STARTROUND)
     * - ENDROUND + ENDGAME => CHAT_ROUND_ENDED_GAME_FINISHED (skip redundant ENDGAME)
     */
    const normalizedEntries: NormalizedEntry[] = [];

    for (let i = 0; i < sortedEntries.length; i++) {
      const entry = sortedEntries[i];
      const next = i + 1 < sortedEntries.length ? sortedEntries[i + 1] : undefined;

      if (
        entry.eventType === 'STARTGAME' &&
        next?.eventType === 'STARTROUND' &&
        (next.payload as { round?: number } | undefined)?.round === 0
      ) {
        normalizedEntries.push({
          ...entry,
          eventType: CHAT_EVENT_TYPES.GAME_STARTED_ROUND_1,
          // Prefer the STARTROUND timestamp (the moment round 1 begins).
          timestamp: next.timestamp,
          chatSyntheticKey: `${entry.timestamp}__${next.timestamp}`,
        });
        i++; // skip STARTROUND
        continue;
      }

      if (entry.eventType === 'ENDROUND' && next?.eventType === 'ENDGAME') {
        const roundValue = (entry.payload as { round?: number } | undefined)?.round;
        normalizedEntries.push({
          ...entry,
          eventType: CHAT_EVENT_TYPES.ROUND_ENDED_GAME_FINISHED,
          // Prefer ENDGAME timestamp (the moment the game finishes).
          timestamp: next.timestamp,
          payload: {
            ...(entry.payload ?? {}),
            round: roundValue,
          },
          chatSyntheticKey: `${entry.timestamp}__${next.timestamp}`,
        });
        i++; // skip ENDGAME
        continue;
      }

      normalizedEntries.push(entry);
    }

    return normalizedEntries.map((entry, index, entries) => {
      const id = entry.chatSyntheticKey
        ? `${entry.eventType}-${entry.senderId}-${entry.chatSyntheticKey}`
        : `${entry.eventType}-${entry.senderId}-${entry.timestamp}`;

      // Handle chat messages (NEWMESSAGE events)
      if (entry.eventType === 'NEWMESSAGE') {
        const prevMessage = index > 0 ? entries[index - 1] : null;
        const nextMessage = index < entries.length - 1 ? entries[index + 1] : null;

        let position: 'first' | 'normal' | 'last' | 'single';

        if (
          !prevMessage ||
          prevMessage.eventType !== 'NEWMESSAGE' ||
          prevMessage.senderId !== entry.senderId
        ) {
          position =
            nextMessage &&
            nextMessage.eventType === 'NEWMESSAGE' &&
            nextMessage.senderId === entry.senderId
              ? 'first'
              : 'single';
        } else if (
          !nextMessage ||
          nextMessage.eventType !== 'NEWMESSAGE' ||
          nextMessage.senderId !== entry.senderId
        ) {
          position = 'last';
        } else {
          position = 'normal';
        }

        const player = players.find((p) => String(p.userId ?? '') === String(entry.senderId ?? ''));
        // Not in current players list (spectator, or left the game)
        const isSpectator = !player;
        const payload = entry.payload as
          | { message?: string; senderName?: string | null }
          | undefined;
        const nameFromPlayer = player?.name?.trim() ?? '';
        const nameFromPayload =
          (typeof payload?.senderName === 'string' && payload.senderName.trim()) || '';
        // Prefer live player name; else persisted sender snapshot (spectators, ex-players)
        const senderDisplayName = nameFromPlayer || nameFromPayload;

        const model: ChatMessageModel = {
          message: entry.payload?.message || '',
          sentTime: new Date(entry.timestamp).toLocaleTimeString(),
          position: position,
          direction: entry.senderId === userId ? 'outgoing' : 'incoming',
          sender: senderDisplayName,
        };

        return {
          type: 'message' as const,
          id,
          timestamp: entry.timestamp,
          model,
          userId: entry.senderId,
          message: entry.payload?.message || '',
          eventLogIndex: entry.eventLogIndex,
          isSpectator,
        };
      }

      // Handle game events
      return {
        type: 'event' as const,
        id,
        timestamp: entry.timestamp,
        eventType: entry.eventType,
        payload: entry.payload,
        senderId: entry.senderId,
      };
    });
  },
);

// Legacy selectors for backward compatibility (filter to only NEWMESSAGE events)
export const selectChatMessages = createSelector(selectEventLog, (eventLog) =>
  eventLog.filter((entry) => entry.eventType === 'NEWMESSAGE'),
);

export const selectChatMessageModels = createSelector(
  selectChatDisplayItems,
  (displayItems) =>
    displayItems.filter((item) => item.type === 'message') as Array<
      ChatDisplayItem & { type: 'message' }
    >,
);

export const selectChatMessagesCount = createSelector(
  selectEventLog,
  (eventLog) => eventLog.filter((entry) => entry.eventType === 'NEWMESSAGE').length,
);

/**
 * Index of the newest incoming message (NEWMESSAGE sent by someone else) inside `eventLog`.
 * Returns -1 when there are no incoming messages.
 */
export const selectChatLatestIncomingEventIndex = createSelector(
  selectEventLog,
  selectUserId,
  (eventLog, userId) => {
    const userIdStr = String(userId ?? '');
    let lastIncomingIdx = -1;
    for (let i = 0; i < eventLog.length; i++) {
      const entry = eventLog[i];
      if (entry.eventType !== 'NEWMESSAGE') continue;
      const senderIdStr = String(entry.senderId ?? '');
      if (senderIdStr && senderIdStr !== userIdStr) {
        lastIncomingIdx = i;
      }
    }
    return lastIncomingIdx;
  },
);

/**
 * Number of unseen incoming chat messages since `chatLastReadIncomingEventIndex`.
 */
export const selectChatUnreadIncomingCount = createSelector(
  selectEventLog,
  selectUserId,
  selectChatLastReadIncomingEventIndex,
  (eventLog, userId, chatLastReadIncomingEventIndex) => {
    const userIdStr = String(userId ?? '');
    const lastRead = Math.min(chatLastReadIncomingEventIndex, eventLog.length - 1);
    let unread = 0;
    for (let i = 0; i < eventLog.length; i++) {
      if (i <= lastRead) continue;
      const entry = eventLog[i];
      if (entry.eventType !== 'NEWMESSAGE') continue;
      const senderIdStr = String(entry.senderId ?? '');
      if (senderIdStr && senderIdStr !== userIdStr) unread++;
    }
    return unread;
  },
);

export const selectChatMessagesSorted = createSelector(selectEventLog, (eventLog) =>
  sortBy(
    eventLog.filter((entry) => entry.eventType === 'NEWMESSAGE'),
    (entry) => new Date(entry.timestamp).getTime(),
  ),
);

/** True if any horse has moved (pos > 0). Stable boolean to avoid mass re-renders when horseFields array reference changes. */
export const selectAnyHorseMoved = createSelector(selectHorsesField, (horsesField) =>
  horsesField.some((field) => field.pos > 0),
);

/**
 * Returns the rank of the specified horse but takes into consideration,
 * that before any horse was moved all horses have the same rank.
 */
export const selectHorseRanksCorrected = createCachedSelector(
  selectAnyHorseMoved,
  selectHorsesHandi,
  selectHorsesRanks,
  (_state: State, horseIndex: number) => horseIndex,
  (anyHorseMoved, horsesHandi, horsesRanks, horseIndex) => {
    const ranks = horsesRanks[horseIndex];
    if (anyHorseMoved) {
      return ranks ?? { rank: -1, totalRank: -1 };
    } else {
      return { rank: horsesHandi[horseIndex] ?? -1, totalRank: ranks?.totalRank ?? -1 };
    }
  },
)((_state, horseIndex) => horseIndex);

export interface HorseStatistic {
  name: string;
  color: number;
  points: number[];
  jockeyName: string;
  pointsSum: number;
  rank: number;
  turnPos: number;
  totalRank: number;
  userId: string;
  playerName: string;
  country: string;
}

export interface PlayerStatistic {
  index: number;
  name: string;
  points: number[];
  pointsSum: number;
  totalRank: number;
}

export const rounds = [...Array(Turfmaster.rounds).keys()];
export const selectHorsesStatistics = createSelector(
  selectRound,
  selectTurnOrder,
  selectPlayers,
  selectHorses,
  selectHorsesPoints,
  selectHorsesRanks,
  (round, turnOrder, players, horses, horsesPoints, horsesRanks): HorseStatistic[] => {
    if (!horses || horses.length === 0) return [];

    const statistics = sortBy(
      horses.map<HorseStatistic>(({ color, name, jockeyName, playerId }, index) => ({
        name,
        color,
        jockeyName,
        points: rounds.map((round) => horsesPoints?.[index]?.[round] ?? 0),
        pointsSum: horsesPoints?.[index]?.reduce((prev, cur) => prev + cur, 0) ?? 0,
        rank: horsesRanks?.[index]?.rank ?? -1,
        totalRank: round > 0 ? (horsesRanks?.[index]?.totalRank ?? 0) : -1,
        turnPos: turnOrder[index],
        userId: players?.[playerId]?.userId,
        playerName: players?.[playerId]?.name,
        country: players?.[playerId]?.country,
      })),
      (item) => (round > 0 ? item.totalRank : item.turnPos),
    );

    return statistics;
  },
);

export const selectResultsMockHorseStatistics = (
  _state: State,
  scenario?: string,
): HorseStatistic[] => getResultsMockData(scenario).horses;

export const selectResultsMockPlayerStatistics = (
  _state: State,
  scenario?: string,
): PlayerStatistic[] => getResultsMockData(scenario).players;

export const selectLastRoundFinished = createSelector(
  selectRound,
  selectHorsesFinished,
  selectCurrentState,
  (round, horsesFinished, currentState) =>
    round >= Turfmaster.rounds - 1 &&
    (currentState === Turfmaster.stateMachine.ENDROUND ||
      currentState === Turfmaster.stateMachine.ENDGAME ||
      horsesFinished.every((horseFinished) => horseFinished)),
);

export const selectPlayersStatistics = createSelector(
  selectPlayers,
  selectHorses,
  selectHorsesPoints,
  (players, horses, horsesPoints): PlayerStatistic[] =>
    sortBy(
      players?.map(({ id, name, totalRank }, index) => {
        const playerHorses = horses.filter(({ playerId }) => playerId === id);

        return {
          index,
          name,
          points: rounds.map((round) =>
            playerHorses.reduce(
              (prev, cur) => prev + Number(horsesPoints?.[cur.index]?.[round]),
              0,
            ),
          ),
          pointsSum: playerHorses.reduce(
            (prev, cur) =>
              prev + Number(horsesPoints?.[cur.index]?.reduce((prev, cur) => prev + cur, 0)),
            0,
          ),
          totalRank,
        };
      }),
      (item) => item.totalRank,
    ),
);

export const selectCurrentHorseIndex = createSelector(
  selectTurnOrder,
  selectTurnPos,
  (turnOrder, turnPos) => turnOrder[turnPos],
);

export const selectCurrentHorse = createSelector(
  selectCurrentHorseIndex,
  selectHorses,
  (currentHorseIndex, horses) => horses[currentHorseIndex],
);

export const selectIsCurrentOwnHorse = createSelector(
  selectPlayerId,
  selectCurrentHorse,
  (playerId, currentHorse) => currentHorse?.playerId === playerId,
);

export const selectOwnHorses = createSelector(selectHorses, selectPlayerId, (horses, playerId) =>
  horses.filter((horse) => horse.playerId === playerId),
);

export const selectIsOwnHorseIndexCurrent = createCachedSelector(
  selectOwnHorses,
  selectCurrentHorseIndex,
  (_state: State, horseIndex: number) => horseIndex,
  (ownHorses, currentHorseIndex, ownHorseIndex) =>
    ownHorses[ownHorseIndex]?.index === currentHorseIndex,
)((_state, horseIndex) => horseIndex);

export const selectIsOwnHorseIndex = createCachedSelector(
  selectPlayerId,
  selectHorses,
  (_state: State, horseIndex: number) => horseIndex,
  (playerId, horses, horseIndex) => horses[horseIndex]?.playerId === playerId,
)((_state, horseIndex) => horseIndex);

export const selectCurrentHorseCards = createSelector(
  selectCurrentHorseIndex,
  selectHorsesCards,
  (currentHorseIndex, horsesCards) => horsesCards[currentHorseIndex] as CardValue[][] | undefined,
);

export const selectCurrentHorseBonuscards = createSelector(
  selectCurrentHorseIndex,
  selectHorsesBonuscards,
  (currentHorseIndex, horsesBonuscards) => horsesBonuscards[currentHorseIndex],
);

export const selectHorseCardsCurrentCount = createCachedSelector(
  selectRound,
  selectHorseCards,
  (round, horseCards) => horseCards?.[round]?.length ?? 0,
)((_state, horseIndex) => horseIndex);

export const selectHorseCardDeck = createStructuredCachedSelector({
  cards: selectHorseCards,
  bonuscards: selectHorseBonuscards,
})((_state, horseIndex) => horseIndex);

export const selectHorseHasBonuscards = createCachedSelector(
  selectHorseBonuscards,
  selectHorseCards,
  (bonusCards, horseCards) =>
    bonusCards === -1 && (horseCards?.[Turfmaster.rounds]?.length ?? 0) > 0,
)((_state, horseIndex) => horseIndex);

export const selectHorseCardDeckCurrentMeta = createStructuredCachedSelector({
  cardsCount: selectHorseCardsCurrentCount,
  hasBonuscards: selectHorseHasBonuscards,
})((_state, horseIndex) => horseIndex);

export const selectCurrentHorseCardDeck = createSelector(
  selectCurrentHorseCards,
  selectCurrentHorseBonuscards,
  (cards, bonuscards) => ({
    cards,
    bonuscards,
  }),
);

export const selectHorseHighlightAllowed = createSelector(
  selectCurrentState,
  selectRound,
  selectIsDiceInteractionPhase,
  (currentState: StateMachineValue, currentRound: number, isDiceInteractionPhase: boolean) => {
    const gameStarted = currentRound > 0 || !isPreGameState(currentState);
    return (
      gameStarted &&
      // prevent brief highlight flicker while transitioning to next turn
      currentState !== Turfmaster.stateMachine.ENDTURN &&
      // prevent horse blinking while last turn in round is shown with a delay
      currentState !== Turfmaster.stateMachine.SELECTMOVEOPP &&
      // prevent horse blinking during dice-selection interactions
      !isDiceInteractionPhase &&
      // prevent horse blinking before first and after last round
      currentRound >= 0 &&
      currentRound < Turfmaster.rounds
    );
  },
);

export const selectIsOwnHighlightedHorse = createCachedSelector(
  selectHorseHighlightAllowed,
  selectCurrentHorseIndex,
  selectPlayerId,
  selectHorses,
  (_state: State, horseIndex: number) => horseIndex,
  (isHighlightAllowed, currentHorseIndex, playerId, horses, horseIndex) =>
    isHighlightAllowed &&
    horses[horseIndex]?.playerId === playerId &&
    currentHorseIndex === horseIndex,
)((_state, horseIndex) => horseIndex);

export const selectIsHighlightedHorse = createCachedSelector(
  selectHorseHighlightAllowed,
  selectCurrentHorseIndex,
  (_state: State, horseIndex: number) => horseIndex,
  (isHighlightAllowed, currentHorseIndex, horseIndex) =>
    isHighlightAllowed && currentHorseIndex === horseIndex,
)((_state, horseIndex) => horseIndex);

export const selectOwnHorse = createCachedSelector(
  selectHorses,
  selectPlayerId,
  (_state: State, ownHorseIndex: number) => ownHorseIndex,
  (horses, playerId, ownHorseIndex) =>
    horses.filter((horse) => horse.playerId === playerId)[ownHorseIndex],
)((_state, ownHorseIndex) => ownHorseIndex);

export const selectCurrentRoundWinnerHorse = createSelector(
  selectRound,
  selectHorsesPoints,
  (round, horsesPoints) => {
    // check if horse has the points a winner should have in this round
    for (const [horseIndex, horsePoints] of horsesPoints.entries())
      if (horsePoints[round] === Turfmaster.points[0]) return horseIndex;
    return -1;
  },
);

export const selectActiveHorsesField = createSelector(
  selectHorsesField,
  selectHorsesFinished,
  selectHorsesDropped,
  (horsesField, horsesFinished, horsesDropped) => {
    return (horsesField || []).filter(
      (_value, index) => !horsesFinished[index] && !horsesDropped[index],
    );
  },
);
